import {
  AssetIoConfigOutput,
  SiteIoConfiguration,
} from "../../../../../services/client";

export type FieldChange = {
  field: string;
  oldValue: unknown;
  newValue: unknown;
};

export type AssetDiffEntry =
  | { kind: "added"; assetId: string; config: AssetIoConfigOutput["config"] }
  | { kind: "removed"; assetId: string; config: AssetIoConfigOutput["config"] }
  | {
      kind: "changed";
      assetId: string;
      oldConfig: AssetIoConfigOutput["config"];
      newConfig: AssetIoConfigOutput["config"];
      changes: FieldChange[];
    };

export type SiteIoDiff = {
  assets: AssetDiffEntry[];
  controllerModeChange?: FieldChange;
  phasePermutationModeChange?: FieldChange;
};

export type SiteIoDraft = {
  asset_io_config: AssetIoConfigOutput[];
  controller_mode?: SiteIoConfiguration["controller_mode"];
  phase_permutation_mode?: SiteIoConfiguration["phase_permutation_mode"];
};

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a === null || b === null) {
    return a === b;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a !== "object") {
    return false;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) {
        return false;
      }
    }
    return true;
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
  for (const k of keys) {
    if (!deepEqual(ao[k], bo[k])) {
      return false;
    }
  }
  return true;
}

export function flattenObject(
  value: unknown,
  prefix = "",
  out: Record<string, unknown> = {},
): Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    out[prefix || "(value)"] = value;
    return out;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) {
    out[prefix || "(empty)"] = {};
    return out;
  }
  for (const [k, v] of entries) {
    const next = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      flattenObject(v, next, out);
    } else {
      out[next] = v;
    }
  }
  return out;
}

function diffFlattenedFields(oldVal: unknown, newVal: unknown): FieldChange[] {
  const oldFlat = flattenObject(oldVal ?? {});
  const newFlat = flattenObject(newVal ?? {});
  const keys = new Set([...Object.keys(oldFlat), ...Object.keys(newFlat)]);
  const changes: FieldChange[] = [];
  for (const k of keys) {
    if (!deepEqual(oldFlat[k], newFlat[k])) {
      changes.push({ field: k, oldValue: oldFlat[k], newValue: newFlat[k] });
    }
  }
  return changes;
}

function indexAssets(
  entries: AssetIoConfigOutput[] | undefined | null,
): Map<string, AssetIoConfigOutput["config"]> {
  const out = new Map<string, AssetIoConfigOutput["config"]>();
  for (const entry of entries ?? []) {
    out.set(entry.asset_id, entry.config);
  }
  return out;
}

export function computeSiteIoDiff(
  serverConfig: SiteIoConfiguration | null | undefined,
  draftConfig: SiteIoDraft | null | undefined,
): SiteIoDiff {
  const serverAssets = indexAssets(serverConfig?.asset_io_config);
  const draftAssets = indexAssets(draftConfig?.asset_io_config);

  const assets: AssetDiffEntry[] = [];
  for (const [assetId, config] of draftAssets) {
    const old = serverAssets.get(assetId);
    if (!old) {
      assets.push({ kind: "added", assetId, config });
    } else if (!deepEqual(old, config)) {
      assets.push({
        kind: "changed",
        assetId,
        oldConfig: old,
        newConfig: config,
        changes: diffFlattenedFields(old, config),
      });
    }
  }
  for (const [assetId, config] of serverAssets) {
    if (!draftAssets.has(assetId)) {
      assets.push({ kind: "removed", assetId, config });
    }
  }

  const hasDraft = draftConfig !== undefined && draftConfig !== null;

  const serverMode = serverConfig?.controller_mode;
  const draftMode = draftConfig?.controller_mode ?? serverMode;
  const controllerModeChange =
    hasDraft && serverMode !== draftMode
      ? {
          field: "controller_mode",
          oldValue: serverMode,
          newValue: draftMode,
        }
      : undefined;

  const serverPhasePermutationMode = serverConfig?.phase_permutation_mode;
  const draftPhasePermutationMode =
    draftConfig?.phase_permutation_mode ?? serverPhasePermutationMode;
  const phasePermutationModeChange =
    hasDraft && serverPhasePermutationMode !== draftPhasePermutationMode
      ? {
          field: "phase_permutation_mode",
          oldValue: serverPhasePermutationMode,
          newValue: draftPhasePermutationMode,
        }
      : undefined;

  return { assets, controllerModeChange, phasePermutationModeChange };
}

export function diffIsEmpty(diff: SiteIoDiff): boolean {
  return (
    diff.assets.length === 0 &&
    !diff.controllerModeChange &&
    !diff.phasePermutationModeChange
  );
}
